/**
 * Utility functions for calculating permission changes in the account form
 */

export interface PermissionChange {
  slug: string
  action: 'added' | 'removed'
}

export interface PermissionChanges {
  added: string[]
  removed: string[]
  hasChanges: boolean
  addedLength: number
  removedLength: number
}

/**
 * Calculate the differences between original and current permission arrays
 */
export const calculatePermissionChanges = (
  originalPermissions: string[],
  currentPermissions: string[]
): PermissionChanges => {
  const originalSlugs = new Set(originalPermissions)
  const currentSlugs = new Set(currentPermissions)

  const added = Array.from(currentSlugs).filter(slug => !originalSlugs.has(slug))
  const removed = Array.from(originalSlugs).filter(slug => !currentSlugs.has(slug))

  return {
    added,
    removed,
    hasChanges: added.length > 0 || removed.length > 0,
    addedLength: added.length,
    removedLength: removed.length,
  }
}

/**
 * Get a detailed list of permission changes with action types
 */
export const getPermissionChangeDetails = (
  originalPermissions: { slug: string }[],
  currentPermissions: { slug: string }[]
): PermissionChange[] => {
  const changes = calculatePermissionChanges(
    originalPermissions.map(p => p.slug),
    currentPermissions.map(p => p.slug)
  )

  const details: PermissionChange[] = []

  changes.added.forEach(slug => {
    details.push({ slug, action: 'added' })
  })

  changes.removed.forEach(slug => {
    details.push({ slug, action: 'removed' })
  })

  return details
}

/**
 * Check if two permission arrays are equal
 */
export const arePermissionsEqual = (
  permissions1: { slug: string }[],
  permissions2: { slug: string }[]
): boolean => {
  if (permissions1.length !== permissions2.length) {
    return false
  }

  const slugs1 = new Set(permissions1.map(p => p.slug))
  const slugs2 = new Set(permissions2.map(p => p.slug))

  if (slugs1.size !== slugs2.size) {
    return false
  }

  return Array.from(slugs1).every(slug => slugs2.has(slug))
}

/**
 * Get only the permissions that were added
 */
export const getAddedPermissions = (
  originalPermissions: { slug: string }[],
  currentPermissions: { slug: string }[]
): { slug: string }[] => {
  const changes = calculatePermissionChanges(
    originalPermissions.map(p => p.slug),
    currentPermissions.map(p => p.slug)
  )
  return changes.added.map(slug => ({ slug }))
}

/**
 * Get only the permissions that were removed
 */
export const getRemovedPermissions = (
  originalPermissions: { slug: string }[],
  currentPermissions: { slug: string }[]
): { slug: string }[] => {
  const changes = calculatePermissionChanges(
    originalPermissions.map(p => p.slug),
    currentPermissions.map(p => p.slug)
  )
  return changes.removed.map(slug => ({ slug }))
}

/**
 * Calculate the differences between original and current role arrays
 */
export const calculateRoleChanges = (
  originalRoles: { value: string; label: string }[],
  currentRoles: { value: string; label: string }[]
): PermissionChanges => {
  const originalIds = new Set(originalRoles.map(r => r.value))
  const currentIds = new Set(currentRoles.map(r => r.value))

  const added = Array.from(currentIds).filter(id => !originalIds.has(id))
  const removed = Array.from(originalIds).filter(id => !currentIds.has(id))

  return {
    added,
    removed,
    hasChanges: added.length > 0 || removed.length > 0,
    addedLength: added.length,
    removedLength: removed.length,
  }
}
